/** Call Scripts (plan E08-T06): the tenant's script apps, and a new one with its secret shown once. */
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { Schemas } from "../api/client";
import { Page, Section, useApiQuery } from "../app/Page";
import { useCan } from "../app/session";
import { useFormat, useT } from "../i18n";
import { Button } from "../ui/Button";
import { Empty } from "../ui/states";
import { Table } from "../ui/Table";
import { NewScriptApp, SecretOnce } from "./parts";

export function ScriptsPage() {
  const t = useT();
  const format = useFormat();
  const canWrite = useCan("agents:write");
  const [adding, setAdding] = useState(false);
  const [made, setMade] = useState<Schemas["ScriptAppCreated"] | null>(null);
  const apps = useApiQuery("scripts", (api) => api.GET("/v1/script-apps", { params: { query: { limit: 100 } } }));
  const create = canWrite && !adding && (
    <Button onClick={() => {
      setMade(null);
      setAdding(true);
    }}>
      <Plus size={16} aria-hidden="true" />
      {t("scripts.new")}
    </Button>
  );
  return (
    <Page title={t("nav.scripts")} description={t("scripts.description")} actions={create}>
      {adding && <NewScriptApp onCreated={(m) => {
        setAdding(false);
        setMade(m);
      }} />}
      {made && (
        <SecretOnce secret={made.secret}>
          <Link to="/scripts/$appId" params={{ appId: made.id }} className="text-sm text-[var(--color-primary)] underline">
            {t("scripts.open", { name: made.name })}
          </Link>
        </SecretOnce>
      )}
      <Section query={apps}>
        {(page) =>
          page.data.length === 0 ? (
            adding ? null : (
              <Empty title={t("scripts.empty")}>
                <p>{t("scripts.emptyBody")}</p>
                {create}
              </Empty>
            )
          ) : (
            <Table
              caption={t("scripts.apps")}
              rows={page.data}
              rowKey={(a) => a.id}
              columns={[
                {
                  header: t("scripts.name"),
                  cell: (a) => (
                    <Link to="/scripts/$appId" params={{ appId: a.id }} className="font-medium text-[var(--color-primary)] underline">
                      {a.name}
                    </Link>
                  ),
                },
                { header: t("scripts.descriptionLabel"), cell: (a) => a.description || "—" },
                {
                  header: t("scripts.live"),
                  cell: (a) => (a.published_version ? <span className="font-mono">v{a.published_version}</span> : t("scripts.notPublished")),
                },
                { header: t("scripts.latest"), cell: (a) => <span className="font-mono">v{a.latest_version}</span> },
                { header: t("scripts.updated"), cell: (a) => format.dateTime(a.updated_at) },
              ]}
            />
          )
        }
      </Section>
    </Page>
  );
}
